import React, { useState } from 'react';
import { CheckCircle, HelpCircle, Calendar, User, Mail, Phone, ArrowRight, ArrowLeft, X } from 'lucide-react';

interface Question {
  id: number;
  text: string;
  options: string[];
}

const AnnuityQuiz: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [showResults, setShowResults] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredTime: ''
  });
  const [showPopup, setShowPopup] = useState(false);

  const questions: Question[] = [
    {
      id: 1,
      text: "What's your primary retirement income goal?",
      options: [
        "Guaranteed monthly income",
        "Maximum growth potential",
        "Balance of growth and security",
        "Preserve principal"
      ]
    },
    {
      id: 2,
      text: "When do you plan to start taking retirement income?",
      options: [
        "Within 5 years",
        "5-10 years",
        "10-15 years",
        "More than 15 years"
      ]
    },
    {
      id: 3,
      text: "How concerned are you about market volatility?",
      options: [
        "Very concerned",
        "Somewhat concerned",
        "Neutral",
        "Not concerned"
      ]
    },
    {
      id: 4,
      text: "What's your current retirement savings allocation?",
      options: [
        "Mostly 401(k)/IRA",
        "Mix of accounts",
        "Mostly cash/savings",
        "Not sure"
      ]
    },
    {
      id: 5,
      text: "How important is leaving a legacy to your heirs?",
      options: [
        "Very important",
        "Somewhat important",
        "Not very important",
        "Not a priority"
      ]
    }
  ];

  const handleAnswer = (answer: string) => {
    setAnswers(prev => ({ ...prev, [questions[currentStep].id]: answer }));
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowResults(true);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle consultation scheduling
    console.log('Form submitted:', { answers, formData });
    setShowPopup(true);
  };

  // Function to handle closing the popup
  const handleClosePopup = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPopup(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {!showResults ? (
          <div className="bg-white rounded-xl shadow-sm p-8">
            {/* Progress bar */}
            <div className="mb-8">
              <div className="h-2 bg-gray-200 rounded-full">
                <div 
                  className="h-2 bg-primary rounded-full transition-all duration-300"
                  style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
                />
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Question {currentStep + 1} of {questions.length}
              </p>
            </div>

            {/* Question */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {questions[currentStep].text}
              </h2>
              <div className="space-y-3">
                {questions[currentStep].options.map((option, index) => (
                  <button
                    key={index}
                    className="w-full text-left p-4 rounded-lg border hover:border-primary hover:bg-primary/5 transition-colors"
                    onClick={() => handleAnswer(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between">
              <button
                onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))}
                disabled={currentStep === 0}
                className="btn btn-outline"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Previous
              </button>
              <div className="text-sm text-gray-500">
                {currentStep + 1} of {questions.length}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                <CheckCircle className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Your Results Are Ready!</h2>
              <p className="text-gray-600 mt-2">
                Schedule a consultation to review your personalized retirement strategy.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    required
                    className="pl-10 block w-full border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    required
                    className="pl-10 block w-full border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                    <Phone className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="tel"
                    required
                    className="pl-10 block w-full border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Best Time to Contact</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                    <Calendar className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    className="pl-10 block w-full border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    value={formData.preferredTime}
                    onChange={(e) => setFormData({ ...formData, preferredTime: e.target.value })}
                  >
                    <option value="">Select a time</option>
                    <option value="morning">Morning (9AM - 12PM)</option>
                    <option value="afternoon">Afternoon (12PM - 4PM)</option>
                    <option value="evening">Evening (4PM - 7PM)</option>
                  </select>
                </div>
              </div>

              <button type="submit" className="w-full btn btn-primary">
                Schedule Consultation
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Success Popup */}
      {showPopup && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            className="bg-white rounded-lg p-8 max-w-md w-full mx-4" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Consultation Scheduled</h3>
              <button 
                onClick={handleClosePopup}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="text-center mb-6">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <p className="text-lg text-gray-700 mb-2">
                Thank you, {formData.name}!
              </p>
              <p className="text-gray-600">
                Your consultation request has been received. We'll contact you at {formData.phone} during your preferred time to confirm your appointment.
              </p>
            </div>
            <button
              onClick={handleClosePopup}
              className="w-full btn btn-primary"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnuityQuiz;